"use client"

import { useState, useEffect, useRef } from "react"
import Image from "next/image"
import Link from "next/link"
import { useGetNewsByTypeIdQuery } from '@/hooks/useGetNewsByTypeIdQueryHook/useGetNewsByTypeIdQuery'
import { Loader2, ArrowLeft, ArrowRight } from "lucide-react"

interface TeamMemberFromAPI {
    id: number
    title: string
    description: string
    image: string
    content?: string
}

// ID danh mục ban điều hành từ biến môi trường
const BOD_ID = Number(process.env.NEXT_PUBLIC_BOD_ID)

export default function MembersSection() {
    // State tiến trình cuộn ngang
    const [progressWidth, setProgressWidth] = useState(0)
    // State kiểm soát khả năng cuộn trái
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    // State kiểm soát khả năng cuộn phải
    const [canScrollRight, setCanScrollRight] = useState(true)

    // Ref container cuộn ngang
    const containerRef = useRef<HTMLDivElement>(null)
    // Ref lưới item hiển thị
    const gridRef = useRef<HTMLDivElement>(null)

    // Hook gọi API lấy danh sách thành viên
    const {
        data: rawMembers = [],
        isLoading,
        isError
    } = useGetNewsByTypeIdQuery({
        newsTypeId: BOD_ID,
        limit: 20,
        enabled: true
    })

    // Hàm tạo slug từ tiêu đề
    const createSlug = (title: string) => {
        return title.toLowerCase()
            .trim()
            .replace(/[áàảãạăắằẳẵặâấầẩẫậ]/g, 'a')
            .replace(/[éèẻẽẹêếềểễệ]/g, 'e')
            .replace(/[iíìỉĩị]/g, 'i')
            .replace(/[óòỏõọôốồổỗộơớờởỡợ]/g, 'o')
            .replace(/[úùủũụưứừửữự]/g, 'u')
            .replace(/[ýỳỷỹỵ]/g, 'y')
            .replace(/đ/g, 'd')
            .replace(/[^a-z0-9-]/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
    };

    // Chuẩn hóa dữ liệu thành viên từ API
    const teamMembers: TeamMemberFromAPI[] = rawMembers.map((item: any) => ({
        id: item.id,
        title: item.title || "Chưa có tên",
        description: item.description || "Chưa có mô tả",
        image: item.image
            ? item.image.replace(/^<|>$/g, '').trim()
            : "/images/placeholder-member.png",
        content: item.content
    }))

    // Hàm điều khiển cuộn ngang theo hướng
    const scrollToDirection = (direction: "left" | "right") => {
        if (!containerRef.current) return

        const container = containerRef.current
        const containerWidth = container.clientWidth

        container.scrollBy({
            left: direction === "left" ? -containerWidth : containerWidth,
            behavior: "smooth"
        })
    }

    // Effect tính toán tiến trình và trạng thái cuộn
    useEffect(() => {
        const calculateProgress = () => {
            if (!containerRef.current || !gridRef.current) return

            const container = containerRef.current
            const grid = gridRef.current

            const containerWidth = container.clientWidth
            const totalWidth = grid.scrollWidth
            const scrollLeft = container.scrollLeft

            const visibleAndScrolled = containerWidth + scrollLeft
            const progress = (visibleAndScrolled / totalWidth) * 100
            setProgressWidth(Math.min(progress, 100))

            setCanScrollLeft(scrollLeft > 1)
            setCanScrollRight(Math.ceil(scrollLeft + containerWidth) < totalWidth)
        }

        const container = containerRef.current
        if (container) {
            container.addEventListener("scroll", calculateProgress)
            window.addEventListener("resize", calculateProgress)

            const timer = setTimeout(calculateProgress, 100)
            return () => {
                clearTimeout(timer)
                container.removeEventListener("scroll", calculateProgress)
                window.removeEventListener("resize", calculateProgress)
            }
        }
    }, [teamMembers])

    // Giao diện trạng thái đang tải dữ liệu
    if (isLoading) {
        return (
            <section className="w-full bg-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 xl:px-8 flex items-center justify-center h-96">
                    <div className="flex items-center gap-3 text-gray-600">
                        <Loader2 className="w-6 h-6 animate-spin" />
                        <span>Đang tải thông tin đội ngũ...</span>
                    </div>
                </div>
            </section>
        )
    }

    // Giao diện trạng thái lỗi hoặc không có dữ liệu
    if (isError || teamMembers.length === 0) {
        return (
            <section className="w-full bg-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 xl:px-8 text-center">
                    <p className="text-gray-500">Không tải được thông tin đội ngũ. Vui lòng thử lại sau.</p>
                </div>
            </section>
        )
    }

    return (
        <section className="w-full bg-white pb-10 xl:pb-6 2xl:pb-12 min-[1920px]:pb-20">
            <div className="max-w-7xl 2xl:max-w-[1450px] min-[1920px]:max-w-[1800px] mx-auto px-4 sm:px-6 xl:px-8">

                {/* Container cuộn ngang danh sách thành viên */}
                <div ref={containerRef} className="w-full overflow-x-auto overflow-y-hidden pb-4 scrollbar-hide">
                    <div ref={gridRef} className="flex gap-4 xl:gap-6 2xl:gap-8 min-[1920px]:gap-12 min-w-min">
                        {teamMembers.map((member) => {
                            // Tạo đường dẫn chi tiết thành viên
                            const slugName = createSlug(member.title);
                            const memberHref = `/news/${slugName}-${member.id}`;

                            return (
                                <Link
                                    key={member.id}
                                    href={memberHref}
                                    className="flex-shrink-0 w-64 xl:w-52 2xl:w-64 min-[1920px]:w-80 group block"
                                >
                                    {/* Khối hiển thị ảnh đại diện */}
                                    <div className="relative w-full h-80 xl:h-64 2xl:h-80 min-[1920px]:h-[450px] bg-gray-100 overflow-hidden cursor-pointer">
                                        <Image
                                            src={member.image}
                                            alt={member.title}
                                            fill
                                            className="object-cover transition-transform duration-500 group-hover:scale-110"
                                            sizes="(max-width: 768px) 256px, (max-width: 1280px) 208px, (max-width: 1536px) 256px, 320px"
                                        />

                                        {/* Lớp overlay hiển thị nội dung chi tiết */}
                                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-start pl-2 pr-4 pb-4">
                                            <div className="text-white w-full ">
                                                <div
                                                    className="text-[9px] xl:text-[10px] 2xl:text-[11px] min-[1920px]:text-[13px] leading-relaxed space-y-1 [&_ul]:list-disc [&_ul]:pl-4 line-clamp-[4] md:line-clamp-[6] xl:line-clamp-[10]"
                                                    style={{ fontFamily: '"Helvetica Neue", Roboto, sans-serif' }}
                                                    dangerouslySetInnerHTML={{
                                                        __html: member.content || '<p>Chưa có thông tin chi tiết.</p>'
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Khối hiển thị thông tin thành viên */}
                                    <div className="mt-3 2xl:mt-4 min-[1920px]:mt-5">
                                        <h3
                                            className="font-medium text-[#2D3746] text-base xl:text-sm 2xl:text-base min-[1920px]:text-xl group-hover:text-[#006633] transition-colors duration-300"
                                            style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}
                                        >
                                            {member.title}
                                        </h3>
                                        <p
                                            className="text-[10px] xl:text-[11px] 2xl:text-[13px] min-[1920px]:text-base text-[#2D3746] mt-1 whitespace-pre-wrap"
                                            style={{ fontFamily: '"Helvetica Neue", Roboto, sans-serif' }}
                                        >
                                            {member.description}
                                        </p>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>

                {/* Khối thanh tiến trình và điều hướng cuộn */}
                <div className="mt-8 xl:mt-14 2xl:mt-16 min-[1920px]:mt-24 flex items-center gap-0">
                    <div className="flex-1">
                        <div className="w-full h-0.5 2xl:h-0.5 min-[1920px]:h-1 relative flex items-center">
                            <div className="absolute w-full h-[0.1px] 2xl:h-[0.5px] bg-black top-1/2 -translate-y-1/2 left-0 z-0"></div>
                            <div
                                className="h-full bg-green-800 transition-all duration-300 ease-out relative z-10"
                                style={{ width: `${progressWidth}%` }}
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-1 2xl:gap-2 ml-0">
                        <button
                            onClick={() => scrollToDirection("left")}
                            disabled={!canScrollLeft}
                            className={`w-10 h-10 xl:w-8 xl:h-8 2xl:w-10 2xl:h-10 min-[1920px]:w-14 min-[1920px]:h-14 rounded-full border border-gray-300 flex items-center justify-center transition-all
                                ${!canScrollLeft
                                    ? 'opacity-50 cursor-not-allowed bg-gray-50'
                                    : 'hover:bg-gray-100 cursor-pointer'
                                }
                            `}
                            aria-label="Previous"
                        >
                            <ArrowLeft className="w-5 h-5 xl:w-4 xl:h-4 2xl:w-5 2xl:h-5 min-[1920px]:w-7 min-[1920px]:h-7 text-gray-600" strokeWidth={1.25} />
                        </button>

                        <button
                            onClick={() => scrollToDirection("right")}
                            disabled={!canScrollRight}
                            className={`w-10 h-10 xl:w-8 xl:h-8 2xl:w-10 2xl:h-10 min-[1920px]:w-14 min-[1920px]:h-14 rounded-full border border-gray-300 flex items-center justify-center transition-all
                                ${!canScrollRight
                                    ? 'opacity-50 cursor-not-allowed bg-gray-50'
                                    : 'hover:bg-gray-100 cursor-pointer'
                                }
                            `}
                            aria-label="Next"
                        >
                            <ArrowRight className="w-5 h-5 xl:w-4 xl:h-4 2xl:w-5 2xl:h-5 min-[1920px]:w-7 min-[1920px]:h-7 text-gray-600" strokeWidth={1} />
                        </button>
                    </div>
                </div>
            </div>

            {/* CSS ẩn thanh cuộn ngang */}
            <style jsx>{`
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
                .scrollbar-hide {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>
        </section>
    )
}